import { useQueryClient } from "@tanstack/react-query";
import { Button, Col, Form, FormProps, Input, Modal, Radio, Row, Select, Switch } from "antd";
import { FC, useEffect } from "react";
import { useNotification } from "~/hooks";
import { TEMPLATE_KEYS, useCreateRule, useUpdateRule, useGetTemplates } from "~/services/template";
import { capitalize } from "~/utils";
import { PreferredCommunicationMethod } from "~/utils/constants";
import { TRule } from "~/types/rule";

type Props = {
  open: boolean;
  onClose: () => void;
  rule?: TRule;
};

enum TriggerType {
  STAFF_ASSIGNED = 'staff_assigned',
  MENTION = 'mention',
  SERVICE_COMPLETED = 'service_completed',
  BOOKING_STATUS_CHANGE = 'booking_status_change',
  SERVICE_STARTING_SOON = 'service_starting_soon',
  AVAILABILITY_REMINDER = 'availability_reminder',
}

enum RecipientType {
  ASSIGNED_STAFF = 'assigned_staff',
  MENTIONED_USERS = 'mentioned_users',
  ALL_ADMINS = 'all_admins',
  ALL_STAFF = 'all_staff',
  BOOKING_CLIENT = 'booking_client',
  SPECIFIC_USERS = 'specific_users',
}

const TRIGGER_LABELS: Record<TriggerType, string> = {
  [TriggerType.STAFF_ASSIGNED]: 'Staff Assigned to Service',
  [TriggerType.MENTION]: 'User @Mentioned in Notes',
  [TriggerType.SERVICE_COMPLETED]: 'Service Ticket Completed',
  [TriggerType.BOOKING_STATUS_CHANGE]: 'Booking Status Changed',
  [TriggerType.SERVICE_STARTING_SOON]: 'Service Starting Soon (24h)',
  [TriggerType.AVAILABILITY_REMINDER]: 'Availability Reminder',
};

const RECIPIENT_LABELS: Record<RecipientType, string> = {
  [RecipientType.ASSIGNED_STAFF]: 'Assigned Staff Members',
  [RecipientType.MENTIONED_USERS]: 'Mentioned Users',
  [RecipientType.ALL_ADMINS]: 'All Admins',
  [RecipientType.ALL_STAFF]: 'All Staff',
  [RecipientType.BOOKING_CLIENT]: 'Booking Client',
  [RecipientType.SPECIFIC_USERS]: 'Specific Users',
};

export const ModalRule: FC<Props> = ({ open, onClose, rule }) => {
  const [form] = Form.useForm();
  const { success, error } = useNotification();

  const qc = useQueryClient();
  const createMutation = useCreateRule();
  const updateMutation = useUpdateRule();
  const { data: templates } = useGetTemplates({});

  const isEditMode = !!rule;

  // Watch form values at the top level
  const triggerType = Form.useWatch('triggerType', form);
  const channels = Form.useWatch('channels', form);
  const recipientType = Form.useWatch('recipientType', form);

  // Populate form when editing
  useEffect(() => {
    if (open && rule) {
      form.setFieldsValue({
        name: rule.name,
        description: rule.description,
        triggerType: rule.triggerType,
        recipientType: rule.recipientType,
        templateId: rule.templateId,
        channels: rule.channels,
        isActive: rule.isActive,
      });
    } else if (open && !rule) {
      form.resetFields();
    }
  }, [open, rule, form]);

  const onSubmit: FormProps["onFinish"] = (values) => {
    const mutation = isEditMode ? updateMutation : createMutation;
    const mutationData = isEditMode
      ? { ruleId: rule!.id, body: values }
      : values;

    mutation.mutate(mutationData as any, {
      onSuccess: () => {
        success({
          message: isEditMode
            ? "Rule updated successfully"
            : "Rule created successfully",
        });
        form.resetFields();
        onClose();
        qc.invalidateQueries({
          queryKey: TEMPLATE_KEYS.GET_RULES(),
        });
      },
      onError: (err) => {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} rule:`, err);
        error({
          message: `Error ${isEditMode ? 'updating' : 'creating'} rule`,
          description: err?.message || "Something went wrong",
        });
      },
    });
  };

  return (
    <Modal
      destroyOnHidden
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      title={
        <div className="py-2">
          <h2 className="text-xl font-medium">
            {isEditMode ? "Edit Notification Rule" : "Create New Notification Rule"}
          </h2>
          <p className="text-sm text-gray-500 font-normal mt-1">
            Define when and who receives email notifications
          </p>
        </div>
      }
      width={{
        xs: "90%",
        sm: "80%",
        md: "70%",
        lg: "60%",
        xl: "50%",
        xxl: "40%",
      }}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              label="Rule Name"
              name="name"
              rules={[{ required: true, message: "Please enter rule name" }]}
            >
              <Input placeholder="e.g., Staff Assignment Notification" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Description" name="description">
              <Input.TextArea rows={2} placeholder="Describe what this rule does" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-2">
              <h3 className="font-medium text-sm mb-2">Trigger Condition</h3>
              <p className="text-xs text-gray-600">When should this notification be sent?</p>
            </div>
            <Form.Item
              name="triggerType"
              rules={[{ required: true, message: "Please select a trigger" }]}
            >
              <Select placeholder="Select when to trigger" size="large">
                {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <div className="p-3 bg-green-50 border border-green-200 rounded mb-2">
              <h3 className="font-medium text-sm mb-2">Recipients</h3>
              <p className="text-xs text-gray-600">Who should receive this notification?</p>
            </div>
            <Form.Item
              name="recipientType"
              rules={[{ required: true, message: "Please select recipients" }]}
            >
              <Select placeholder="Select who receives the notification" size="large">
                {Object.entries(RECIPIENT_LABELS).map(([value, label]) => (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24}>
            <div className="p-3 bg-purple-50 border border-purple-200 rounded mb-2">
              <h3 className="font-medium text-sm mb-2">Notification Settings</h3>
            </div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item
                  label="Template (Optional)"
                  name="templateId"
                  help="You can create a template later and assign it to this rule"
                >
                  <Select placeholder="Select email template or leave blank" size="large" allowClear>
                    {templates?.items.map((template) => (
                      <Select.Option key={template.id} value={template.id}>
                        {template.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Channel"
                  name="channels"
                  initialValue={PreferredCommunicationMethod.EMAIL}
                >
                  <Select size="large">
                    {Object.values(PreferredCommunicationMethod).map((channel) => (
                      <Select.Option key={channel} value={channel}>
                        {capitalize(channel)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Active"
              name="isActive"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
            <p className="text-xs text-gray-500 -mt-2">
              Enable or disable this rule without deleting it
            </p>
          </Col>

          <Col span={24}>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded text-xs">
              <strong>Rule Summary:</strong> When{" "}
              <span className="text-blue-600">
                {triggerType
                  ? TRIGGER_LABELS[triggerType as TriggerType]
                  : '[select trigger]'}
              </span>{" "}
              happens, send via{" "}
              <span className="text-purple-600">
                {channels || 'EMAIL'}
              </span>{" "}
              to{" "}
              <span className="text-green-600">
                {recipientType
                  ? RECIPIENT_LABELS[recipientType as RecipientType]
                  : '[select recipients]'}
              </span>
            </div>
          </Col>
        </Row>
        <Button
          type="primary"
          htmlType="submit"
          loading={createMutation.isPending || updateMutation.isPending}
          block
          className="mt-4"
          size="large"
        >
          {isEditMode ? "Update Notification Rule" : "Create Notification Rule"}
        </Button>
      </Form>
    </Modal>
  );
};
